import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
    cx, IconCheques, IconCustomers, IconMore, IconReports, IconSettings, IconSignOut, IconStock, IconToday, IconUser, Kbd, ThemeSwitch, Wordmark,
    useModal,
} from '@timely/ui';
import { holds, type TenantContext } from '@timely/domain';
import { useSession } from '../auth/SessionProvider';
import { supabase } from '../lib/supabaseClient';
import { useActiveWorkspace, useWorkspaces } from './WorkspaceProvider';
import { shouldShowSwitcher } from './resolve';
import { CompanyMark } from '../branding/CompanyMark';
import { titled, useDocumentTitle } from '../lib/documentTitle';
import { paths, primaryDestinations, screenName, visibleSettings, type PrimaryPlace } from './navigation';
import { useNavCounts } from '../book/useToday';
import { ShortcutsDialog, useShortcutHelp, useShortcuts } from './Shortcuts';
import { CompanySwitcher, type SwitchedState } from './CompanySwitcher';

export { screenName } from './navigation';

const PLACE_ICON: Record<PrimaryPlace, () => React.ReactNode> = {
    today: () => <IconToday />,
    customers: () => <IconCustomers />,
    cheques: () => <IconCheques />,
    reports: () => <IconReports />,
    stock: () => <IconStock />,
};

/**
 * The chrome around every screen inside a company (PRODUCT-UX-FOUNDATION §4–5,
 * DESIGN-SYSTEM §10).
 *
 * One navigation, laid out for the screen it is on. From 1024px it is a
 * sidebar, so the content gets the width of the monitor. Narrower, the daily
 * places — Today, Customers, Cheques, Reports — are a bar at the bottom, under
 * the thumb, where somebody working down a queue reaches for them; Stock,
 * Settings, the company switch and signing out are one press further, under
 * More. The bar and the sidebar are the same links, restyled, so there is one
 * set of places to keep right.
 */
/** The bottom bar's name for a place: its label without the scope ("My reports" → "Reports"). */
const shortLabel = (label: string) => label.replace(/^My (\w)/, (_, first: string) => first.toUpperCase());

export function WorkspaceShell() {
    const context = useActiveWorkspace();
    const { contexts } = useWorkspaces();
    const { email, signOut } = useSession();
    const { workspace } = context;
    const location = useLocation();
    const navigate = useNavigate();
    const { pathname } = location;
    const arrivedBySwitch = (location.state as Partial<SwitchedState> | null)?.switchedCompany === true;
    const heading = useRef<HTMLHeadingElement>(null);
    const lastPath = useRef<string | null>(null);
    const stock = useStockConnected(context);
    const [moreOpen, setMoreOpen] = useState(false);
    const [switchOpen, setSwitchOpen] = useState(false);

    const places = primaryDestinations(context, { connected: stock });
    const settings = visibleSettings(context);
    const to = paths(workspace.slug);
    const switcher = shouldShowSwitcher(contexts);

    // What is waiting, beside the place it waits in (DESIGN-SYSTEM §10). Today's figure is late follow-ups: amber,
    // because red is kept for money past its due date and for failures (Customer 360, 02 §19.2).
    const counts = useNavCounts(context, places.some(d => d.place === 'cheques'));
    const waiting: Partial<Record<PrimaryPlace, { count: number; words: string; tone: 'dang' | 'warn' }>> = {
        ...(counts.overdue ? { today: { count: counts.overdue, words: 'late follow-ups', tone: 'warn' as const } } : {}),
        ...(counts.cheques ? { cheques: { count: counts.cheques, words: 'to bank or chase', tone: 'warn' as const } } : {}),
    };

    // A screen may take the frame's two phone bars for itself (an account: its way back, and Call · Record outcome ·
    // Remind). It renders into these two places; the app's own bottom bar steps aside while it does.
    const [topSlot, setTopSlot] = useState<HTMLDivElement | null>(null);
    const [bottomSlot, setBottomSlot] = useState<HTMLDivElement | null>(null);
    const [claimed, setClaimed] = useState<ChromeClaim>(UNCLAIMED);
    const chrome = useMemo<ScreenChrome>(() => ({ top: topSlot, bottom: bottomSlot, claim: setClaimed }), [topSlot, bottomSlot]);

    const shortcutPlaces = {
        today: to.today,
        customers: to.customers,
        cheques: places.some(d => d.place === 'cheques') ? to.cheques : null,
        reports: places.some(d => d.place === 'reports') ? to.reports : null,
        settings: settings.length > 0 ? to.settings : null,
    };
    const [helpOpen, showHelp, hideHelp] = useShortcutHelp();
    useShortcuts(shortcutPlaces, showHelp);

    const screen = screenName(pathname, workspace.slug);
    useDocumentTitle(titled(screen, workspace.name));

    useEffect(() => {
        // Moving between screens in place announces nothing to a screen
        // reader. Focus goes to the new page's heading — only when the path
        // really changed, not on the first load and not when an effect merely
        // runs twice. Arriving in another company through Switch company is
        // this shell's first load (the company's screens start afresh), and
        // says where it arrived the same way.
        if ((lastPath.current !== null && lastPath.current !== pathname) || (lastPath.current === null && arrivedBySwitch)) {
            heading.current?.focus();
        }
        // Said once. The browser keeps a page's state through a reload, and a reload is an ordinary load: it
        // starts at the top, where Tab finds "Skip to content" and the sidebar first.
        if (arrivedBySwitch) navigate(`${pathname}${location.search}${location.hash}`, { replace: true, state: null });
        // Moving to another screen closes the More sheet. Only a real move: on the first load the sheet is already
        // closed, and a late first-load effect must never close a sheet somebody has just opened (a race seen under load).
        if (lastPath.current !== null && lastPath.current !== pathname) setMoreOpen(false);
        lastPath.current = pathname;
    }, [pathname, arrivedBySwitch]);

    // Stock and Settings live in the sidebar from 1024px, and under More below it.
    const inMore = (place: PrimaryPlace | 'settings') => place === 'stock' || place === 'settings';
    const moreActive = pathname.startsWith(to.settings) || pathname.startsWith(to.stock) || pathname.startsWith(to.account);

    const item = (active: boolean, secondary: boolean) => cx(
        'relative transition-colors duration-150',
        // below 1024: a tab in the bottom bar, on the navy frame
        'max-lg:flex-1 max-lg:min-w-0 max-lg:flex max-lg:flex-col max-lg:items-center max-lg:justify-center max-lg:gap-1 max-lg:h-14 max-lg:text-caption max-lg:font-semibold',
        active ? 'max-lg:text-chrome-label' : 'max-lg:text-chrome-label-3',
        secondary && 'max-lg:hidden',
        // from 1024: a row in the sidebar
        'lg:flex lg:items-center lg:gap-3 lg:h-10 lg:px-3 lg:rounded-control lg:text-body',
        active
            ? 'lg:bg-chrome-3 lg:text-chrome-label lg:font-semibold'
            : 'lg:font-medium lg:text-chrome-label-2 lg:hover:bg-chrome-2 lg:hover:text-chrome-label',
    );
    /** The current place's mark: a jade rule on the sidebar row's left edge, a short jade bar over a tab. */
    const current = (active: boolean) => active && (
        <span aria-hidden="true" className="absolute rounded-full bg-mark-on-chrome lg:left-0 lg:top-2.5 lg:bottom-2.5 lg:w-0.5 max-lg:top-0 max-lg:left-1/2 max-lg:-translate-x-1/2 max-lg:h-0.5 max-lg:w-7" />
    );
    const footItem = (active = false) => cx(
        'w-full flex items-center gap-2.5 h-9 px-3 rounded-control text-body font-medium transition-colors duration-150 hover:bg-chrome-2 hover:text-chrome-label',
        active ? 'bg-chrome-3 text-chrome-label' : 'text-chrome-label-2',
    );

    return (
        <div className="min-h-dvh bg-bg">
            <a
                href="#main"
                className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2.5 focus:rounded-control focus:bg-card focus:text-label focus:shadow-e2"
            >
                Skip to content
            </a>

            {/* The navy frame: the sidebar from 1024px; a top bar and a bottom bar below it. */}
            <header className={cx(
                'bg-chrome text-chrome-label',
                'lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 lg:flex lg:flex-col lg:z-30 lg:border-r lg:border-chrome-line',
                // A screen's own way back never scrolls away (Customer 360, decision 1).
                claimed.top && 'max-lg:sticky max-lg:top-0 max-lg:z-30',
            )}>
                {/* The product, then the company. A customer's name never replaces Timely Payment's. */}
                <div className="flex items-center gap-3 px-4 h-14 lg:h-auto lg:flex-col lg:items-stretch lg:gap-5 lg:px-4 lg:pt-6 lg:pb-3">
                    {/* Below 1024px, a screen that takes the frame puts its way back here, in place of the wordmark. */}
                    <div ref={setTopSlot} className={cx('lg:hidden min-w-0', !claimed.top && 'hidden')} />
                    <div className={cx('lg:px-2 flex-none', claimed.top && 'max-lg:hidden')}><Wordmark cut="small" onChrome className="h-4 lg:h-4.5" /></div>
                    <div className="min-w-0 flex items-center gap-2.5 max-lg:ml-auto max-lg:justify-end lg:rounded-control lg:bg-chrome-2 lg:border lg:border-chrome-line lg:px-2.5 lg:py-2">
                        <CompanyMark name={workspace.name} logoPath={workspace.logoPath ?? null} onChrome />
                        <span className="truncate min-w-0 text-secondary font-medium text-chrome-label-2 lg:text-chrome-label">{workspace.name}</span>
                        {/* No switcher when there is only one company (owner decision D2). */}
                        {switcher && (
                            <button
                                type="button"
                                onClick={() => setSwitchOpen(true)}
                                aria-haspopup="dialog"
                                aria-expanded={switchOpen}
                                aria-label="Switch company"
                                className="flex-none ml-auto text-secondary font-semibold text-chrome-label-3 hover:text-chrome-label px-1 py-2 -my-2 max-lg:hidden"
                            >
                                Switch
                            </button>
                        )}
                    </div>
                </div>

                {(places.length > 1 || settings.length > 0) && (
                    <nav
                        aria-label="Sections"
                        className={cx(
                            // below 1024: fixed to the bottom, the width of the screen
                            'max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 max-lg:z-30 max-lg:flex max-lg:bg-chrome max-lg:border-t max-lg:border-chrome-line max-lg:pb-[env(safe-area-inset-bottom)]',
                            // from 1024: the sidebar's list
                            'lg:flex-1 lg:flex lg:flex-col lg:gap-1 lg:px-3 lg:py-2 lg:overflow-y-auto',
                            // below 1024, a screen's own bar takes its place (the account's Call · Record outcome · Remind)
                            claimed.bottom && 'max-lg:hidden',
                        )}
                    >
                        {places.map(d => {
                            const wait = waiting[d.place];
                            return (
                                <NavLink key={d.place} to={to[d.place]} className={({ isActive }) => item(isActive, inMore(d.place))}>
                                    {({ isActive }) => (
                                        <>
                                            {current(isActive)}
                                            <span className="flex-none" aria-hidden="true">{PLACE_ICON[d.place]()}</span>
                                            {/* A tab is 64px wide on a 320px phone: the bar names the place ("Customers"), and the page's own
                                                title keeps the scope ("My customers"). Only one of the two is ever shown, so it is the link's name. */}
                                            <span data-label="bar" className="lg:hidden max-w-full truncate px-0.5 tracking-tight">{shortLabel(d.label)}</span>
                                            <span data-label="full" className="max-lg:hidden">{d.label}</span>
                                            {wait && (
                                                <span className={cx(
                                                    'num text-caption font-semibold rounded-sm px-1.5 leading-5',
                                                    wait.tone === 'dang' ? 'bg-dang-solid text-on-accent' : 'bg-signal-warn text-chrome',
                                                    'lg:ml-auto max-lg:absolute max-lg:top-1.5 max-lg:left-1/2 max-lg:ml-2',
                                                )}>
                                                    {wait.count > 99 ? '99+' : wait.count}
                                                    <span className="sr-only"> {wait.words}</span>
                                                </span>
                                            )}
                                        </>
                                    )}
                                </NavLink>
                            );
                        })}
                        {settings.length > 0 && (
                            <NavLink to={to.settings} className={({ isActive }) => cx(item(isActive, true), 'lg:mt-auto')}>
                                {({ isActive }) => (
                                    <>
                                        {current(isActive)}
                                        <span className="flex-none" aria-hidden="true"><IconSettings /></span>
                                        <span>Settings</span>
                                    </>
                                )}
                            </NavLink>
                        )}
                        <button
                            type="button"
                            aria-haspopup="dialog"
                            aria-expanded={moreOpen}
                            onClick={() => setMoreOpen(true)}
                            className={cx(item(moreActive, false), 'lg:hidden')}
                        >
                            {current(moreActive)}
                            <span className="flex-none" aria-hidden="true"><IconMore /></span>
                            <span>More</span>
                        </button>
                    </nav>
                )}

                <div className="max-lg:hidden border-t border-chrome-line px-3 pt-3 pb-4 space-y-1">
                    <div className="flex items-center justify-between gap-2 px-3 pb-1.5">
                        <span className="text-caption font-medium text-chrome-label-3">Appearance</span>
                        <ThemeSwitch tone="chrome" />
                    </div>
                    <button
                        type="button"
                        onClick={showHelp}
                        aria-keyshortcuts="?"
                        className="w-full flex items-center justify-between gap-2.5 h-9 px-3 rounded-control text-secondary font-medium text-chrome-label-3 hover:bg-chrome-2 hover:text-chrome-label"
                    >
                        Keyboard shortcuts <Kbd className="bg-chrome-2 border-chrome-line text-chrome-label-2">?</Kbd>
                    </button>
                    <div className="px-3 pt-1 pb-1 text-caption text-chrome-label-3 truncate" title={email ?? undefined}>{email}</div>
                    <NavLink to={to.account} className={({ isActive }) => footItem(isActive)}>
                        <span aria-hidden="true"><IconUser /></span>
                        Your account
                    </NavLink>
                    <button type="button" onClick={() => { void signOut(); }} className={footItem()}>
                        <span aria-hidden="true"><IconSignOut /></span>
                        Sign out
                    </button>
                </div>
            </header>

            {moreOpen && (
                <MoreSheet onClose={() => setMoreOpen(false)}>
                    <ul className="py-1">
                        {places.filter(d => inMore(d.place)).map(d => (
                            <li key={d.place}>
                                <NavLink to={to[d.place]} className={SHEET_ITEM}>
                                    <span aria-hidden="true">{PLACE_ICON[d.place]()}</span>{d.label}
                                </NavLink>
                            </li>
                        ))}
                        {settings.length > 0 && (
                            <li>
                                <NavLink to={to.settings} className={SHEET_ITEM}>
                                    <span aria-hidden="true"><IconSettings /></span>Settings
                                </NavLink>
                            </li>
                        )}
                        {switcher && (
                            <li>
                                <button
                                    type="button"
                                    aria-haspopup="dialog"
                                    onClick={() => { setMoreOpen(false); setSwitchOpen(true); }}
                                    className={SHEET_ITEM}
                                >
                                    <span aria-hidden="true"><IconCustomers /></span>Switch company
                                </button>
                            </li>
                        )}
                    </ul>
                    <div className="border-t border-separator px-4 py-3 flex items-center justify-between flex-wrap gap-x-3 gap-y-2">
                        <span className="text-secondary font-medium text-label-2">Appearance</span>
                        <ThemeSwitch />
                    </div>
                    <div className="border-t border-separator pt-2 mt-1">
                        <div className="px-4 pb-1 text-secondary text-label-3 truncate">{email}</div>
                        <NavLink to={to.account} className={SHEET_ITEM}>
                            <span aria-hidden="true"><IconUser /></span>Your account
                        </NavLink>
                        <button type="button" onClick={() => { void signOut(); }} className={SHEET_ITEM}>
                            <span aria-hidden="true"><IconSignOut /></span>Sign out
                        </button>
                    </div>
                </MoreSheet>
            )}

            {helpOpen && <ShortcutsDialog places={shortcutPlaces} onClose={hideHelp} />}

            {switchOpen && <CompanySwitcher contexts={contexts} currentSlug={workspace.slug} onClose={() => setSwitchOpen(false)} />}

            {/* Below 1024px, a screen that takes the frame puts its own bar here, where the app's bottom bar was. */}
            <div ref={setBottomSlot} className={cx('lg:hidden', !claimed.bottom && 'hidden')} />

            <div className={cx('lg:pl-64', claimed.bottom ? 'max-lg:pb-[calc(4.5rem+env(safe-area-inset-bottom))]' : 'max-lg:pb-[calc(3.5rem+env(safe-area-inset-bottom))]')}>
                {/* The page's one first-level heading. The screens' own titles are
                    second-level, under it; this names the page for a screen reader
                    and is where focus lands after moving between screens. */}
                <h1 ref={heading} tabIndex={-1} className="sr-only">{screen}, {workspace.name}</h1>
                <ScreenChromeContext.Provider value={chrome}>
                    <Outlet />
                </ScreenChromeContext.Provider>
            </div>
        </div>
    );
}

const SHEET_ITEM = 'w-full flex items-center gap-3 h-12 px-4 text-body font-medium text-label hover:bg-hover';

/**
 * The two places a screen may take in the frame below 1024px (Customer 360, decision 1): `top`, the navy top bar,
 * for its way back; `bottom`, for its own bar in place of the app's. A screen claims them while it is shown — the top
 * bar then stays on screen, and the app's bottom bar steps aside only for a screen with a bar of its own — and renders
 * into them through a portal.
 */
export interface ChromeClaim {
    top: boolean;
    bottom: boolean;
}

const UNCLAIMED: ChromeClaim = { top: false, bottom: false };

export interface ScreenChrome {
    top: HTMLElement | null;
    bottom: HTMLElement | null;
    claim: (claimed: ChromeClaim) => void;
}

export const ScreenChromeContext = createContext<ScreenChrome>({ top: null, bottom: null, claim: () => {} });

/**
 * Takes the frame's phone bars for as long as the calling screen is shown, and gives them back after: the top bar
 * always, the bottom one only when the screen has a bar to put there (`bottom`), so nobody is left with an empty bar
 * in place of the app's.
 */
export function useScreenChrome({ bottom = true }: { bottom?: boolean } = {}): Pick<ScreenChrome, 'top' | 'bottom'> {
    const chrome = useContext(ScreenChromeContext);
    const { claim } = chrome;
    useEffect(() => {
        claim({ top: true, bottom });
        return () => claim(UNCLAIMED);
    }, [claim, bottom]);
    return { top: chrome.top, bottom: bottom ? chrome.bottom : null };
}

/** The More sheet: rises over the bottom bar, holds the places that are not daily, and closes on Esc or a press outside. */
function MoreSheet({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
    const panel = useModal(true, onClose, { closeOnEscape: true });
    return (
        <div className="lg:hidden fixed inset-0 z-40 bg-overlay animate-fade flex items-end" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div
                ref={panel}
                role="dialog"
                aria-modal="true"
                aria-label="More"
                className="w-full bg-card rounded-t-dialog border-t border-separator pb-[calc(0.5rem+env(safe-area-inset-bottom))] max-h-[80dvh] overflow-y-auto animate-sheet"
            >
                {children}
            </div>
        </div>
    );
}

/**
 * Whether this company has a stock sheet, for the navigation.
 *
 * `stock_connected()` (0032) answers anybody who may see stock — a Collector
 * included, who cannot read the data source — with one yes or no and nothing
 * about the sheet. Until it answers, or if it cannot, the answer is unknown and
 * Stock stays: hiding a place somebody may need on a failed request is worse
 * than showing one they do not.
 */
function useStockConnected(context: TenantContext): boolean | null {
    const workspaceId = context.workspace.id;
    const canSee = holds(context, 'stock.view');
    const [connected, setConnected] = useState<boolean | null>(null);
    useEffect(() => {
        if (!canSee) { setConnected(null); return; }
        let current = true;
        void supabase().rpc('stock_connected', { p_workspace: workspaceId })
            .then(({ data, error }) => { if (current) setConnected(error || typeof data !== 'boolean' ? null : data); });
        return () => { current = false; };
    }, [workspaceId, canSee]);
    return connected;
}

/* --------------------------------- layout ---------------------------------- */

/**
 * How wide a page may grow (PRODUCT-UX-FOUNDATION §6). Lists, queues and
 * reports use the monitor; a customer's page is two columns; forms and text
 * keep a readable measure.
 */
export type PageWidth = 'wide' | 'standard' | 'form';

const WIDTH: Record<PageWidth, string> = {
    wide: 'max-w-page',
    standard: 'max-w-6xl',
    form: 'max-w-3xl',
};

/** Set by a layout that already frames its pages (Settings), so a page inside it does not frame itself again. */
export const PageFrame = createContext(false);

/** The page frame every screen inside the shell uses, so widths and gutters agree. */
export const Page = ({ children, width = 'wide' }: { children: React.ReactNode; width?: PageWidth }) => {
    const framed = useContext(PageFrame);
    // A form inside Settings is a little wider than a form alone: its groups put their names beside their fields (FormSection).
    if (framed) return <div className={cx('min-w-0', width === 'form' && 'max-w-4xl', width === 'standard' && 'max-w-5xl')}>{children}</div>;
    return (
        <main id="main" tabIndex={-1} className={cx('mx-auto w-full px-4 sm:px-6 lg:px-8 pt-5 pb-8 lg:pt-7 lg:pb-10 focus:outline-none', WIDTH[width])}>
            {children}
        </main>
    );
};
